import {
  Pwerm2CalculationDashboardTotalEquityChartYearDto,
  Pwerm2CalculationDashboardTotalEquityChartYearInstrumentBlockDto,
  Pwerm2CalculationDashboardTotalEquityChartYearInstrumentBlockOwnershipDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import {
  OpmForecastDto,
  OpmStackedDistributionPlotDataPointDto,
  OpmForecastInstrumentDto,
  OpmForecastInstrumentOwnershipDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import {
  PerYearCapitalStructureChartStructure,
  PerYearCapitalStructureChartRank,
  PerYearCapitalStructureChartInstrumentOwnership,
  PerYearCapitalStructureChartInstrument,
} from './per-year-capital-structure-chart-types';
import {
  NET_DEBT_RANK,
  NET_DEBT_TYPE,
} from '@app/modules/projects/dashboard/pwerm2-widgets/total-equity/constants';
import {
  InstrumentBlockKey,
  InstrumentRank,
} from '@app/shared/models/contracts/enums/shared-enums';
import { l } from '@locale/setupI18n';

export interface PerYearCapitalStructureChartYearData {
  forecastDate: string;
  weightedEv: number;
  presentValueOfTotalEquity: number;
  proceedsByOwner: Record<string, number>;
  sponsorMoM?: Nullable<number>;
  sponsorIRR?: Nullable<number>;
}

type InstrumentWithoutProportions = Omit<
  PerYearCapitalStructureChartInstrument,
  'proportionOfType' | 'proportionOfRank'
>;

function aggregateProceedsByOwner(
  instrumentProceedsByOwner:
    | Dictionary<Pwerm2CalculationDashboardTotalEquityChartYearInstrumentBlockOwnershipDto>
    | Dictionary<OpmForecastInstrumentOwnershipDto>
): Dictionary<PerYearCapitalStructureChartInstrumentOwnership> {
  const proceedsByOwner: Dictionary<PerYearCapitalStructureChartInstrumentOwnership> = {};
  Object.entries(instrumentProceedsByOwner).forEach(([ownerId, ownership]) => {
    proceedsByOwner[ownerId] = {
      proceeds: ownership.proceeds,
      percentageOfNetExitProceeds: ownership.proceedsPercentageOfNetExitProceeds,
      percentageOfTotalEquityProceeds: ownership.proceedsPercentageOfTotalEquityProceeds,
    };
  });

  return proceedsByOwner;
}

function mapInstrumentToChartInstrument(
  instrument:
    | Pwerm2CalculationDashboardTotalEquityChartYearInstrumentBlockDto
    | OpmForecastInstrumentDto
): PerYearCapitalStructureChartInstrument {
  const proceedsByOwner = aggregateProceedsByOwner(instrument.proceedsByOwner);

  return {
    id: instrument.id,
    narrative: instrument.narrative,
    type: instrument.type,
    proportionOfType: instrument.proportionOfType,
    totalProceeds: instrument.totalProceeds,
    proceedsByOwner,
    isSweet: instrument.isSweet,
    totalProceedsPercentageOfNetExitProceeds: instrument.totalProceedsPercentageOfNetExitProceeds,
    totalProceedsPercentageOfTotalEquityProceeds:
      instrument.totalProceedsPercentageOfTotalEquityProceeds,
    proportionOfRank: instrument.proportionOfRank,
    instrumentIRR: instrument.instrumentIRR ?? 0,
    instrumentMoM: instrument.instrumentMoM,
  };
}

function calculateProceedsByOwnerFromRanks(
  ranks: PerYearCapitalStructureChartRank[]
): Dictionary<number> {
  const proceedsByOwner: Dictionary<number> = {};

  ranks.forEach((rank) => {
    rank.instruments.forEach((instrument) => {
      Object.entries(instrument.proceedsByOwner).forEach(([ownerId, ownership]) => {
        if (!proceedsByOwner[ownerId]) {
          proceedsByOwner[ownerId] = 0;
        }
        proceedsByOwner[ownerId] += ownership.proceeds;
      });
    });
  });

  return proceedsByOwner;
}

/**
 * Transform PWERM calculation data to generic Total Equity Modal structure
 */
export function transformPwermToChartStructure(
  pwermData: Pwerm2CalculationDashboardTotalEquityChartYearDto
): PerYearCapitalStructureChartStructure {
  const ranks: PerYearCapitalStructureChartRank[] = pwermData.ranks.map((rank) => ({
    rank: rank.rank,
    totalProceeds: rank.totalProceeds,
    proportionOfProceeds: rank.proportionOfProceeds,
    instruments: rank.instruments.map(mapInstrumentToChartInstrument),
  }));

  return {
    ranks,
    forecastDate: pwermData.forecastDate,
    totalProceeds: pwermData.totalProceeds,
    presentValueOfTotalEquity: pwermData.presentValueOfTotalEquity,
    proceedsByOwner: pwermData.proceedsByOwner,
    cumulativePvOfInterimCashFlows: pwermData.cumulativePvOfInterimCashFlows,
    weightedEv: pwermData.weightedEv,
    sponsorMoM: pwermData.sponsorMoM,
    sponsorIRR: pwermData.sponsorIRR,
    netExitProceeds: pwermData.netExitProceeds,
  };
}

/**
 * Transform OPM calculation data to generic Total Equity Modal structure
 */
export function transformOpmToChartStructure(
  opmData: OpmForecastDto
): PerYearCapitalStructureChartStructure {
  const ranks: PerYearCapitalStructureChartRank[] = Object.values(opmData.ranks).map((rank) => ({
    rank: rank.rank,
    totalProceeds: rank.totalProceeds,
    proportionOfProceeds: rank.proportionOfProceeds,
    instruments: Object.values(rank.instruments).map(mapInstrumentToChartInstrument),
  }));

  const proceedsByOwner = calculateProceedsByOwnerFromRanks(ranks);

  return {
    ranks,
    forecastDate: opmData.forecastDate,
    totalProceeds: opmData.totalEquityProceeds,
    presentValueOfTotalEquity: opmData.totalNetExitProceeds,
    cumulativePvOfInterimCashFlows: opmData.interimDistribution,
    proceedsByOwner,
  };
}

/**
 * Controls Net Debt visibility on a chart structure destined for the shared
 * `PerYearCapitalStructureModal`. When Net Debt is included, both
 * `presentValueOfTotalEquity` and `totalProceeds` (used by
 * `_ExitDistributionTitle` in the modal header) are corrected to
 * `netDebtInclusiveTotal` so the header total matches the ranks shown -
 * these two fields are always corrected together, never independently, so
 * they share a single required value rather than two loose optional ones.
 */
export type NetDebtVisibility =
  | { includeNetDebt: false }
  | { includeNetDebt: true; netDebtInclusiveTotal: number };

export function applyNetDebtVisibilityToChartStructure(
  structure: PerYearCapitalStructureChartStructure,
  visibility: NetDebtVisibility
): PerYearCapitalStructureChartStructure {
  if (!visibility.includeNetDebt) {
    return {
      ...structure,
      ranks: structure.ranks.filter((rank) => rank.rank !== NET_DEBT_RANK),
    };
  }

  return {
    ...structure,
    presentValueOfTotalEquity: visibility.netDebtInclusiveTotal,
    totalProceeds: visibility.netDebtInclusiveTotal,
  };
}

/**
 * Total Equity Chart (PWERM and OPM) never shows Net Debt - shorthand for
 * `applyNetDebtVisibilityToChartStructure(structure, { includeNetDebt: false })`.
 */
export function excludeNetDebtFromChartStructure(
  structure: PerYearCapitalStructureChartStructure
): PerYearCapitalStructureChartStructure {
  return applyNetDebtVisibilityToChartStructure(structure, { includeNetDebt: false });
}

export function transformOpmForecastTooltipData(
  forecastData: OpmForecastDto[],
  forecastIndex?: number
): PerYearCapitalStructureChartYearData {
  const forecast = forecastData[forecastIndex ?? 0];

  return {
    forecastDate: forecast.forecastDate,
    weightedEv: forecast.exitValue,
    presentValueOfTotalEquity: forecast.totalEquityProceeds,
    proceedsByOwner: forecast.proceedsByOwner,
    sponsorMoM: forecast.institutionalMoM,
    sponsorIRR: forecast.institutionalIrr,
  };
}

export function transformOpmStackedPointToChartStructure(
  pointData: OpmStackedDistributionPlotDataPointDto,
  exitDate: string
): PerYearCapitalStructureChartStructure {
  const rankMap = new Map<number, InstrumentWithoutProportions[]>();

  pointData.instrumentDistributions.forEach((instrument) => {
    const rank = instrument.instrumentRank ?? InstrumentRank.OrdinaryEquity;

    if (!rankMap.has(rank)) {
      rankMap.set(rank, []);
    }

    const proceedsByOwner: Dictionary<PerYearCapitalStructureChartInstrumentOwnership> = {};

    Object.entries(instrument.ownershipSplit ?? {}).forEach(([ownerId, ownership]) => {
      proceedsByOwner[ownerId] = {
        proceeds: ownership.amount,
        percentageOfNetExitProceeds: ownership.percentageOfNetExitProceeds,
        percentageOfTotalEquityProceeds: 0,
      };
    });

    rankMap.get(rank)!.push({
      id: instrument.instrumentId,
      narrative: instrument.instrumentNarrative,
      type: instrument.instrumentType,
      totalProceeds: instrument.distributionAmount,
      proceedsByOwner,
      isSweet: instrument.instrumentIsSweetEquity,
      totalProceedsPercentageOfNetExitProceeds: instrument.distributionPercentageOfNetExitProceeds,
      totalProceedsPercentageOfTotalEquityProceeds:
        instrument.distributionPercentageOfTotalEquityProceeds,
      instrumentIRR: instrument.instrumentIRR ?? 0,
      instrumentMoM: instrument.instrumentMoM ?? 0,
    });
  });

  if (pointData.netDebt !== undefined && pointData.netDebt !== 0) {
    rankMap.set(NET_DEBT_RANK, [
      {
        id: InstrumentBlockKey.NetDebtId,
        narrative: l('_NetDebtOrNetCashNegative'),
        totalProceeds: pointData.netDebt ?? 0,
        proceedsByOwner: {},
        isSweet: false,
        totalProceedsPercentageOfNetExitProceeds: pointData.netDebtPercentageOfNetExitProceeds ?? 0,
        totalProceedsPercentageOfTotalEquityProceeds:
          pointData.netDebtPercentageOfNetExitProceeds ?? 0,
        instrumentIRR: 0,
        instrumentMoM: 0,
        type: NET_DEBT_TYPE,
      },
    ]);
  }

  const totalEquityProceeds = Array.from(rankMap.entries())
    .filter(([rank]) => rank !== NET_DEBT_RANK)
    .reduce(
      (sum, [, instruments]) =>
        sum + instruments.reduce((rankSum, inst) => rankSum + inst.totalProceeds, 0),
      0
    );

  const ranks: PerYearCapitalStructureChartRank[] = Array.from(rankMap.entries())
    .filter(([rank, instruments]) => rank === NET_DEBT_RANK || instruments.length > 0)
    .sort(([a], [b]) => a - b)
    .map(([rank, instruments]) => {
      const rankTotalProceeds =
        rank === NET_DEBT_RANK
          ? pointData.netDebt ?? 0
          : instruments.reduce((sum, inst) => sum + inst.totalProceeds, 0);
      const totalProceedsByType = new Map<string, number>();
      instruments.forEach((inst) => {
        const currentTotal = totalProceedsByType.get(inst.type) ?? 0;
        totalProceedsByType.set(inst.type, currentTotal + inst.totalProceeds);
      });

      const instrumentsWithProportions = instruments.map((inst) => {
        const totalForType = totalProceedsByType.get(inst.type) ?? 0;

        return {
          ...inst,
          proportionOfType: totalForType > 0 ? inst.totalProceeds / totalForType : 0,
          proportionOfRank: rankTotalProceeds > 0 ? inst.totalProceeds / rankTotalProceeds : 0,
        };
      });

      return {
        rank,
        totalProceeds: rankTotalProceeds,
        proportionOfProceeds:
          rank === NET_DEBT_RANK || totalEquityProceeds === 0
            ? undefined
            : rankTotalProceeds / totalEquityProceeds,
        instruments: instrumentsWithProportions,
      };
    });

  const proceedsByOwner = calculateProceedsByOwnerFromRanks(ranks);

  return {
    ranks,
    forecastDate: exitDate,
    totalProceeds: pointData.equityProceeds ?? 0,
    presentValueOfTotalEquity: pointData.totalEquityProceeds ?? 0,
    cumulativePvOfInterimCashFlows: pointData.interimDistribution ?? 0,
    netExitProceeds: pointData.netExitProceeds ?? 0,
    proceedsByOwner,
  };
}
